import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import PantallaDeCarga from './components/PantallaDeCarga';
import { canAccessRoute, getDefaultRouteForUser } from './lib/permissions';
import { BrandLogo, resolveStudioBrand } from './lib/branding';
import { usePasswordSetupIntent } from './lib/recoveryIntent';
import { tomarAutorizacionPendiente } from './lib/oauthPendiente';
import { tomarAccionPendiente } from './lib/sofiaAccionPendiente';
import './styles/globals.css';

// ── LAZY LOADING ── cada página se carga solo cuando se navega a ella ─────────
const Layout         = lazy(() => import('./components/Layout/Layout'));
const Login          = lazy(() => import('./pages/Login'));
const Dashboard      = lazy(() => import('./pages/Dashboard'));
const CRM            = lazy(() => import('./pages/CRM'));
const Kanban         = lazy(() => import('./pages/Kanban'));
const Finanzas       = lazy(() => import('./pages/Finanzas'));
const Admin          = lazy(() => import('./pages/Admin'));
const Portal         = lazy(() => import('./pages/Portal'));
const PhaseGallery   = lazy(() => import('./pages/Portal/PhaseGallery'));
const RevisionPublicaDeFases = lazy(() => import('./pages/Portal/RevisionPublicaDeFases'));
const ClientPortal   = lazy(() => import('./pages/ClientPortal'));
const Contratos      = lazy(() => import('./pages/Contratos'));
const PostVenta      = lazy(() => import('./pages/PostVenta'));
const Inteligencia   = lazy(() => import('./pages/Inteligencia'));
const Marketing      = lazy(() => import('./pages/Marketing'));
const Mensajes       = lazy(() => import('./pages/Mensajes'));
const CotizacionesV2 = lazy(() => import('./features/quotes-v2/CotizacionesV2'));
const Notificaciones = lazy(() => import('./pages/Notificaciones'));
const Novedades      = lazy(() => import('./pages/Novedades'));
const SaludOperacional = lazy(() => import('./pages/SaludOperacional'));
const CotizacionPublicLanding = lazy(() => import('./pages/Cotizaciones/CotizacionPublicLanding'));
const PublicBooking  = lazy(() => import('./pages/PublicBooking'));
const Calendario     = lazy(() => import('./pages/Calendario'));
const PagoCompletado = lazy(() => import('./pages/PagoCompletado'));
const VerifyContract = lazy(() => import('./pages/VerifyContract'));
const OAuthConsent   = lazy(() => import('./pages/OAuthConsent'));
const SofiaAutorizar = lazy(() => import('./pages/SofiaAutorizar'));

// ── PANTALLA DE CARGA ─────────────────────────────────────────────────────────
function getStoredTheme() {
  if (typeof window === 'undefined') return 'dark';
  return window.localStorage.getItem('feria_theme') || 'light';
}

// La pantalla de carga vive en `components/PantallaDeCarga` para que el Portal del
// cliente muestre EXACTAMENTE la misma mientras su identidad no resuelve, en vez de
// montar una segunda propia. Un solo estado visual de carga en toda la app.
const Loading = PantallaDeCarga;

function ProfileErrorScreen() {
  const { user, retryProfile, signOut, loading } = useAuth();
  const brand = resolveStudioBrand({ theme: getStoredTheme() });
  return (
    <div style={{ minHeight:'100vh', background:'var(--dark)', color:'var(--text)', display:'grid', placeItems:'center', padding:24 }}>
      <div
        className="app-bootstrap-card"
        style={{ width:'min(440px, 100%)', background:'var(--s2)', border:'1px solid var(--border-s)', borderRadius:14, padding:28, textAlign:'center' }}
      >
        <div style={{ display:'flex', justifyContent:'center', marginBottom:8 }}>
          <BrandLogo brand={brand} variant="full" imgStyle={{ height: 34, maxWidth: 210 }} fallbackStyle={{ fontSize: 24, color: 'var(--gold)' }} />
        </div>
        <div style={{ fontSize:18, fontWeight:800, marginBottom:8 }}>No pudimos cargar tu perfil</div>
        <div style={{ fontSize:13, color:'var(--text-m)', lineHeight:1.65, marginBottom:18 }}>
          Tu sesion esta activa, pero no podemos confirmar tu rol operativo. Por seguridad no abrimos el portal ni los modulos internos hasta recuperar tu perfil.
        </div>
        {user?.email && (
          <div style={{ fontSize:11, color:'var(--text-d)', marginBottom:18, overflowWrap:'anywhere' }}>
            Cuenta: {user.email}
          </div>
        )}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <button
            type="button"
            onClick={retryProfile}
            disabled={loading}
            style={{ border:'1px solid var(--accent)', background:'var(--gold)', color:'var(--accent-contrast)', borderRadius:9, padding:'10px 12px', fontWeight:800, cursor:loading ? 'wait' : 'pointer', fontFamily:'inherit' }}
          >
            {loading ? 'Reintentando...' : 'Reintentar'}
          </button>
          <button
            type="button"
            onClick={signOut}
            style={{ border:'1px solid var(--border-s)', background:'transparent', color:'var(--text-m)', borderRadius:9, padding:'10px 12px', fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}
          >
            Cerrar sesion
          </button>
        </div>
      </div>
    </div>
  );
}

// ── RUTA PROTEGIDA ────────────────────────────────────────────────────────────
function Protected({ path, element, module }) {
  const { user } = useAuth();
  if (!user || !canAccessRoute(user, path)) {
    return <Navigate to={getDefaultRouteForUser(user)} replace />;
  }
  // Cada módulo tiene su propio ErrorBoundary — un fallo no rompe el resto del app
  return <ErrorBoundary module={module}>{element}</ErrorBoundary>;
}

// ── RUTAS INTERNAS (equipo) ───────────────────────────────────────────────────
function AppRoutes() {
  const { user, loading } = useAuth();
  const setup = usePasswordSetupIntent();

  if (loading) return <Loading />;
  if (!user)   return <Navigate to="/login" replace />;
  // Una sesion de recuperacion NO es una sesion de trabajo. Mientras la contrasena
  // no se haya cambiado, la unica pantalla posible es /login — tambien si el
  // usuario escribe una ruta a mano. La salida no es una trampa: la pantalla
  // ofrece «Salir», el logout limpia la intencion y esta ademas caduca sola.
  if (setup.kind === 'recovery') return <Navigate to="/login" replace />;
  // Un enlace de recuperacion MUERTO no vuelve a /login: Auth ignora `redirect_to`
  // cuando el token ya no vale y suelta al usuario en la raiz del Site URL con
  // `#error=...`. Con sesion viva eso lo metia derecho al portal y el aviso no se
  // veia NUNCA. Medido en produccion el 2026-08-15. No hay bucle: la pantalla de
  // login ofrece «Ir a mi cuenta», que limpia el aviso antes de volver.
  if (setup.linkError) return <Navigate to="/login" replace />;
  if (user.profileLoadError || user.role === 'profile_error' || user.perms === 'profile_error') {
    return <ProfileErrorScreen />;
  }

  if (user.isClient) {
    return (
      <Routes>
        <Route path="/*" element={
          <ErrorBoundary module="Portal cliente" audience="client">
            <ClientPortal />
          </ErrorBoundary>
        } />
      </Routes>
    );
  }

  return (
    <Layout>
      <Suspense fallback={<div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, color:'var(--text-d)' }}>Cargando…</div>}>
        <Routes>
          <Route path="/"             element={<Protected path="/"             module="Dashboard"    element={<Dashboard />}    />} />
          <Route path="/crm"          element={<Protected path="/crm"          module="CRM"          element={<CRM />}          />} />
          <Route path="/kanban"       element={<Protected path="/kanban"       module="Proyectos"    element={<Kanban />}       />} />
          <Route path="/finanzas"     element={<Protected path="/finanzas"     module="Finanzas"     element={<Finanzas />}     />} />
          <Route path="/contratos"    element={<Protected path="/contratos"    module="Contratos"    element={<Contratos />}    />} />
          {/* V2 ES LA PANTALLA DE COTIZACIONES.
              Dejo de ser canario: se monta en la ruta de siempre, que es a la
              que apuntan el menu, el CRM, las notificaciones y los enlaces de
              los correos. Nadie tiene que aprenderse una URL nueva.

              El motor `EditorCotizacion` SIGUE VIVO: V2 lo monta dentro de su
              shell. Lo que deja de ser alcanzable es la PANTALLA legacy, no el
              motor que nacio dentro de ella. */}
          <Route path="/cotizaciones" element={<Protected path="/cotizaciones" module="Cotizaciones" element={<CotizacionesV2 />} />} />
          {/* Los enlaces guardados del canario siguen funcionando, pero no como
              segunda autoridad: redirigen. Una sola pantalla, una sola ruta. */}
          <Route path="/cotizaciones-v2" element={<Navigate to="/cotizaciones" replace />} />
          <Route path="/admin"        element={<Protected path="/admin"        module="Admin"        element={<Admin />}        />} />
          <Route path="/portal"       element={<Protected path="/portal"       module="Portal"       element={<Portal />}       />} />
          <Route path="/galeria-fases" element={<Protected path="/galeria-fases" module="Galería de fases" element={<PhaseGallery />} />} />
          <Route path="/postventa"    element={<Protected path="/postventa"    module="Post-venta"   element={<PostVenta />}    />} />
          <Route path="/inteligencia" element={<Protected path="/inteligencia" module="Inteligencia" element={<Inteligencia />} />} />
          <Route path="/marketing"    element={<Protected path="/marketing"    module="Marketing"    element={<Marketing />}    />} />
          <Route path="/mensajes"     element={<Protected path="/mensajes"     module="Mensajes"     element={<Mensajes />}     />} />
          <Route path="/calendario"   element={<Protected path="/calendario"   module="Calendario"   element={<Calendario />}   />} />
          <Route path="/notificaciones" element={<Protected path="/notificaciones" module="Notificaciones" element={<Notificaciones />} />} />
          <Route path="/novedades"    element={<Protected path="/novedades"    module="Novedades"    element={<Novedades />}    />} />
          <Route path="/salud"        element={<Protected path="/salud"        module="Salud Operacional" element={<SaludOperacional />} />} />
          <Route path="*"             element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

// ── LOGIN ─────────────────────────────────────────────────────────────────────
function LoginRoute() {
  const { user, loading } = useAuth();
  // Antes esto leia `window.location` aqui mismo. Para cuando esta linea corre,
  // supabase-js ya limpio el hash: `type=recovery` no existe y una sesion de
  // recuperacion se veia igual que un login normal. La foto vive en
  // `lib/recoveryIntent` y se toma antes de que se cree el cliente.
  const setup = usePasswordSetupIntent();
  if (loading) return <Loading />;
  if (user && !setup.kind && !setup.linkError) {
    // DOT ↔ SOFÍA: una autorización OAuth que esperaba el login vuelve a su pantalla (una sola vez).
    const pendiente = tomarAutorizacionPendiente();
    if (pendiente) return <Navigate to={`/oauth/consent?authorization_id=${encodeURIComponent(pendiente)}`} replace />;
    // SOFÍA · una acción que esperaba la autorización del Owner vuelve a su pantalla tras el login (una sola vez).
    const accion = tomarAccionPendiente();
    if (accion) return <Navigate to={`/sofia/autorizar?accion=${encodeURIComponent(accion)}`} replace />;
    return <Navigate to={getDefaultRouteForUser(user)} replace />;
  }
  return <Login />;
}

// ── ROOT ──────────────────────────────────────────────────────────────────────
function getRouterBasename() {
  const publicUrl = process.env.PUBLIC_URL;
  if (!publicUrl || publicUrl === '.') return undefined;
  try {
    const pathname = new URL(publicUrl, window.location.origin).pathname.replace(/\/$/, '');
    return pathname || undefined;
  } catch {
    return publicUrl.replace(/\/$/, '') || undefined;
  }
}

export default function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <BrowserRouter basename={getRouterBasename()}>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/login"           element={<LoginRoute />} />
              {/* DOT ↔ SOFÍA · PHASE 01: la pantalla de consentimiento del servidor OAuth de Supabase Auth.
                  Fuera del Layout del equipo: la abre el navegador del flujo OAuth del Dot. */}
              <Route path="/oauth/consent"   element={
                <ErrorBoundary module="Autorizacion OAuth" audience="internal">
                  <OAuthConsent />
                </ErrorBoundary>
              } />
              {/* SOFÍA · autorizar UNA acción externa (enlace que SOFÍA manda por Telegram). Fuera del Layout. */}
              <Route path="/sofia/autorizar" element={
                <ErrorBoundary module="Autorizacion SOFIA" audience="internal">
                  <SofiaAutorizar />
                </ErrorBoundary>
              } />
              <Route path="/pago-completado" element={
                <ErrorBoundary module="Pago completado" audience="client">
                  <PagoCompletado />
                </ErrorBoundary>
              } />
              <Route path="/verify/contract/:code" element={
                <ErrorBoundary module="Verificacion contrato" audience="client">
                  <VerifyContract />
                </ErrorBoundary>
              } />
              {/* REVISION VISUAL DEL OWNER, SIN SESION DE ADMIN.
                  El token de Vercel abre el despliegue, no la aplicacion, y el
                  owner caia en el login con diecisiete pantallas listas. Esta
                  ruta pinta FIXTURES y solo existe como pantalla en PREVIEW: la
                  decide `revisionPublicaDeFases`, que exige el entorno horneado
                  Y que el dominio no sea el de produccion. `/galeria-fases`
                  sigue detras de `Protected`, intacta. */}
              <Route path="/revision-de-fases" element={
                <ErrorBoundary module="Revision de fases" audience="internal">
                  <RevisionPublicaDeFases />
                </ErrorBoundary>
              } />
              <Route path="/cotizacion" element={
                <ErrorBoundary module="Cotizacion publica" audience="client">
                  <CotizacionPublicLanding />
                </ErrorBoundary>
              } />
              <Route path="/quote/:slug" element={
                <ErrorBoundary module="Cotizacion publica" audience="client">
                  <CotizacionPublicLanding />
                </ErrorBoundary>
              } />
              <Route path="/agenda/cotizacion/:quoteId" element={
                <ErrorBoundary module="Agenda publica" audience="client">
                  <PublicBooking />
                </ErrorBoundary>
              } />
              <Route path="/agenda/reunion" element={
                <ErrorBoundary module="Agenda publica" audience="client">
                  <PublicBooking />
                </ErrorBoundary>
              } />
              <Route path="/agenda/reunion/:meetingType" element={
                <ErrorBoundary module="Agenda publica" audience="client">
                  <PublicBooking />
                </ErrorBoundary>
              } />
              <Route path="/*" element={<AppRoutes />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AppProvider>
    </AuthProvider>
  );
}
